import { useEffect, useMemo, useRef, useState } from 'react';
import { buildContext, createChat, trimHistory } from '../ai.js';
import { inline } from '../analyze.js';
import { useRepo } from '../useStore.js';

/* One conversation per space, kept in meta rather than as records.
 *
 * Deliberately not synced. A chat is scaffolding — you talk, you take the word
 * you needed, you move on — and the sync tables are for writing. Nothing said
 * here is worth resurrecting on another device, and nothing said here should
 * end up in a backup of the book. */
const keyFor = spaceId => 'chat:' + spaceId;

/** Replies are plain prose, so the renderer is: paragraphs, and lists. */
function reply(text) {
  let html = '', inList = false;
  for (const line of (text || '').split('\n')) {
    if (/^\s*[-*]\s+/.test(line)) {
      if (!inList) { html += '<ul>'; inList = true; }
      html += '<li>' + inline(line.replace(/^\s*[-*]\s+/, '')) + '</li>';
      continue;
    }
    if (inList) { html += '</ul>'; inList = false; }
    if (/\S/.test(line)) html += '<p>' + inline(line.replace(/^#+\s+/, '')) + '</p>';
  }
  return html + (inList ? '</ul>' : '');
}

const OPENERS = [
  'A better word for…',
  'What did I call…',
  'Look up…'
];

export default function Chat({ space, doc, chat: injected }) {
  const repo = useRepo();
  const chat = useMemo(() => injected || createChat(), [injected]);
  const key = keyFor(space.id);

  const [messages, setMessages] = useState(() => repo.getMeta(key) || []);
  const [input, setInput] = useState('');
  const [live, setLive] = useState(null);   // { text, note } while a reply arrives
  const [error, setError] = useState(null);
  const abort = useRef(null);
  const scroller = useRef(null);
  const asked = useRef('');

  const remember = next => { setMessages(next); repo.setMeta(key, trimHistory(next)); };

  // Follow the answer as it arrives, and the question as it is asked.
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, live]);

  useEffect(() => () => abort.current?.abort(), []);

  async function send(question) {
    const text = question.trim();
    if (!text || live) return;

    const next = [...messages, { role: 'user', text }];
    remember(next);
    setInput('');
    setError(null);
    setLive({ text: '', note: 'Thinking…' });

    const controller = new AbortController();
    abort.current = controller;
    asked.current = '';

    try {
      const answer = await chat.ask({
        messages: next,
        context: buildContext(space, doc),
        signal: controller.signal,
        onEvent: ev => {
          if (ev.t === 'text') { asked.current = ev.text; setLive({ text: ev.text, note: null }); }
          else if (ev.t === 'search') setLive(l => ({ ...l, note: ev.v ? 'Searching: ' + ev.v : 'Searching the web…' }));
          else if (ev.t === 'error') setError(ev.v);
        }
      });
      if (answer) remember([...next, { role: 'assistant', text: answer }]);
    } catch (err) {
      // A stopped answer is still an answer — keep whatever arrived, and say
      // nothing more about it. Anything else is worth reporting.
      if (controller.signal.aborted) {
        if (asked.current) remember([...next, { role: 'assistant', text: asked.current }]);
      } else {
        setError(err.message);
      }
    } finally {
      abort.current = null;
      setLive(null);
    }
  }

  const onKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
  };

  return (
    <div className="chat">
      <div className="chat-log" ref={scroller}>
        {!messages.length && !live && (
          <div className="chat-blank">
            <p>Ask about this space. A word you can&rsquo;t reach for, a fact, something
              you wrote earlier and can&rsquo;t find.</p>
            <p className="chat-blank-rule">It reads your writing. It never changes it.</p>
            <div className="chat-openers">
              {OPENERS.map(o => (
                <button key={o} className="chat-opener" onClick={() => setInput(o.replace('…', ' '))}>{o}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          m.role === 'user'
            ? <div className="chat-said" key={i}>{m.text}</div>
            : <div className="chat-reply" key={i}>
                <div dangerouslySetInnerHTML={{ __html: reply(m.text) }} />
                <button
                  className="chat-copy"
                  onClick={() => navigator.clipboard?.writeText(m.text)}
                >Copy</button>
              </div>
        ))}

        {live && (
          <div className="chat-reply is-live">
            {live.note && <div className="chat-note">{live.note}</div>}
            {live.text && <div dangerouslySetInnerHTML={{ __html: reply(live.text) }} />}
          </div>
        )}

        {error && <div className="chat-error">{error}</div>}
      </div>

      <div className="chat-ask">
        <textarea
          className="chat-input" aria-label="Your question" rows="2"
          placeholder="Ask…  ⏎ to send"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <div className="chat-actions">
          {messages.length > 0 && !live && (
            <button className="link-btn" onClick={() => remember([])}>Clear</button>
          )}
          <div className="spacer" />
          {live
            ? <button className="ghost-btn" onClick={() => abort.current?.abort()}>Stop</button>
            : <button className="ghost-btn is-send" disabled={!input.trim()} onClick={() => send(input)}>Ask</button>}
        </div>
      </div>
    </div>
  );
}
